import { motion } from "framer-motion"
import { InfoIcon } from "lucide-react"
import { type CSSProperties, type ReactNode } from "react"

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"

type PillSelectOption = {
  id: string
  label: string
  count?: number
  mark?: ReactNode
  info?: string
  color?: string
  isDisabled?: boolean
  ariaLabel?: string
}

type BaseProps = {
  options: PillSelectOption[]
  size?: "sm" | "md"
  className?: string
  ariaLabel?: string
}

type PillSelectProps =
  | (BaseProps & {
      mode: "single"
      value: string
      onChange: (id: string) => void
      // A filter with no reset chip: re-clicking the lit chip reports "".
      isClearable?: boolean
    })
  | (BaseProps & {
      mode: "multiple"
      value: string[]
      onChange: (ids: string[]) => void
    })

// text-xs pairs with the type-* role: ToggleGroupItem's base text-sm is grouped
// + dropped by tailwind-merge against this ladder size, while the custom type-*
// utility (which merge can't see) would otherwise lose the cascade and leave the
// pills at 14px instead of the 12px floor. See company-list-row for the same idiom.
const sizeClassName: Record<"sm" | "md", string> = {
  sm: "type-micro text-xs min-h-6 gap-1 px-2 py-1",
  md: "type-micro text-xs min-h-8 gap-1.5 px-2.5 py-1.5",
}

const PILL_TAP_TRANSITION = {
  type: "spring",
  stiffness: 600,
  damping: 15,
  mass: 0.5,
} as const

const pillClassName = (option: PillSelectOption, size: "sm" | "md") =>
  cn(
    "h-auto min-w-0 flex-initial justify-center rounded-sm border",
    "transition-colors duration-(--duration-fast) ease-(--ease-terminal)",
    sizeClassName[size],
    option.color
      ? cn(
          "border-transparent",
          "bg-[color-mix(in_srgb,var(--pill-hue)_14%,transparent)] text-[color:var(--pill-hue)]",
          "pill-hairline [--pill-hairline-color:var(--pill-hue)]",
          "hover:text-foreground hover:bg-[color-mix(in_srgb,var(--pill-hue)_22%,transparent)]"
        )
      : cn(
          "border-transparent bg-card text-muted-foreground",
          "hover:text-foreground hover:bg-accent"
        ),
    "data-[state=on]:border-transparent data-[state=on]:bg-primary data-[state=on]:font-semibold data-[state=on]:text-primary-foreground data-[state=on]:shadow-none",
    "focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-none focus-visible:ring-inset",
    "disabled:cursor-not-allowed disabled:opacity-40"
  )

const PillInfoIcon = ({ info }: { info: string }) => {
  const { theme } = useTerminalTheme()

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          aria-label={info}
          className="inline-flex shrink-0 cursor-default items-center rounded-sm text-current opacity-70 transition-opacity hover:opacity-100"
          onClick={(event) => event.stopPropagation()}
        >
          <InfoIcon aria-hidden="true" className="size-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        collisionPadding={12}
        data-terminal-theme={theme}
        className={cn(
          "type-tooltip bg-popover border-border text-foreground shadow-tooltip max-w-60 rounded-sm border px-2.5 py-2",
          theme === "dark" && "dark"
        )}
      >
        {info}
      </TooltipContent>
    </Tooltip>
  )
}

const listClassName = "flex w-full flex-wrap justify-start gap-1.5"

export const PillSelect = (props: PillSelectProps) => {
  const { options, size = "md", className, ariaLabel } = props

  const items = options.map((option) => (
    <ToggleGroupItem
      key={option.id}
      value={option.id}
      disabled={option.isDisabled}
      aria-label={option.ariaLabel ?? option.label}
      asChild
      style={
        option.color
          ? ({ "--pill-hue": option.color } as CSSProperties)
          : undefined
      }
      className={pillClassName(option, size)}
    >
      <motion.button
        type="button"
        whileTap={option.isDisabled ? undefined : { scale: 0.95 }}
        transition={PILL_TAP_TRANSITION}
      >
        {option.mark}
        {option.info && <PillInfoIcon info={option.info} />}
        <span className="min-w-0 truncate">{option.label}</span>
        {option.count != null && (
          <span className="type-count opacity-70">{option.count}</span>
        )}
      </motion.button>
    </ToggleGroupItem>
  ))

  if (props.mode === "single") {
    return (
      <ToggleGroup
        type="single"
        value={props.value}
        // Radix reports "" when the lit pill is re-clicked; single-select table
        // tabs must always keep one member, so ignore the deselect unless the
        // group is clearable.
        onValueChange={(next) => {
          if (next || props.isClearable) props.onChange(next)
        }}
        aria-label={ariaLabel}
        className={cn(listClassName, className)}
      >
        <TooltipProvider delayDuration={300}>{items}</TooltipProvider>
      </ToggleGroup>
    )
  }

  return (
    <ToggleGroup
      type="multiple"
      value={props.value}
      onValueChange={(next) => props.onChange(next)}
      aria-label={ariaLabel}
      className={cn(listClassName, className)}
    >
      <TooltipProvider delayDuration={300}>{items}</TooltipProvider>
    </ToggleGroup>
  )
}
